import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, type EhrRow, type BuildRow } from '../lib/api.js';

type EndpointType = 'ehr' | 'build' | 'file';

const SOURCE_TYPES: { type: string; label: string; icon: string; description: string; disabled?: boolean }[] = [
  { type: 'ehr',   label: 'External EHR', icon: '⬢', description: 'Pull via recipe' },
  { type: 'build', label: 'Cerbo Build',  icon: '⎔', description: 'Another Cerbo instance' },
  { type: 'file',  label: 'Local File',   icon: '⊞', description: 'CSV or export file' },
];

const DEST_TYPES: { type: string; label: string; icon: string; description: string; disabled?: boolean }[] = [
  { type: 'build', label: 'Cerbo Build',  icon: '⎔', description: 'Import into a build' },
  { type: 'optim', label: 'OptiMantra',   icon: '◌', description: 'Coming soon', disabled: true },
  { type: 'file',  label: 'CSV Export',   icon: '⊞', description: 'Export to file only' },
];

function TypeSelector({
  value,
  onChange,
  options,
}: {
  value: EndpointType;
  onChange: (t: EndpointType) => void;
  options: { type: string; label: string; icon: string; description: string; disabled?: boolean }[];
}) {
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, 1fr)` }}>
      {options.map(({ type, label, icon, description, disabled }) => {
        const active = value === type;
        return (
          <button
            key={type}
            type="button"
            onClick={() => !disabled && onChange(type as EndpointType)}
            disabled={disabled}
            className="flex flex-col items-start gap-1 rounded-xl p-2.5 text-left transition-all duration-100"
            style={disabled ? {
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border)',
              opacity: 0.45,
              cursor: 'not-allowed',
            } : active ? {
              background: 'linear-gradient(135deg, rgba(13,207,184,0.14) 0%, rgba(6,182,212,0.14) 100%)',
              border: '1px solid rgba(13,207,184,0.4)',
            } : {
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs"
                style={active && !disabled ? {
                  background: 'linear-gradient(135deg, #0dcfb8, #06b6d4)',
                  color: '#fff',
                } : {
                  color: 'var(--text-dimmed)',
                  background: 'var(--bg-secondary)',
                }}
              >
                {icon}
              </span>
              <span className="text-[11px] font-semibold leading-tight" style={{ color: active && !disabled ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {label}
              </span>
            </div>
            <span className="text-[10px] leading-tight" style={{ color: 'var(--text-dimmed)' }}>
              {description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</div>
      {children}
      {hint && <div className="text-[10px] mt-1" style={{ color: 'var(--text-dimmed)' }}>{hint}</div>}
    </label>
  );
}

function Select({ value, onChange, required, children }: { value: string; onChange: (v: string) => void; required?: boolean; children: React.ReactNode }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full rounded-lg px-3 py-1.5 text-sm outline-none"
      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
    >
      {children}
    </select>
  );
}

function TextInput({ value, onChange, placeholder, type, required, mono }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; required?: boolean; mono?: boolean;
}) {
  return (
    <input
      type={type ?? 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoComplete="off"
      className={`w-full rounded-lg px-3 py-1.5 text-sm outline-none ${mono ? 'font-mono' : ''}`}
      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
    />
  );
}

function WipBadge({ label }: { label: string }) {
  return (
    <div className="rounded-lg px-3 py-2 text-[11px]" style={{ background: 'rgba(255,209,102,0.08)', color: 'var(--accent-yellow)', border: '1px solid rgba(255,209,102,0.2)' }}>
      {label}
    </div>
  );
}

export function NewMigrationForm({
  onCreated,
  onCancel,
  initialBuildId,
}: {
  onCreated: (sessionId: string) => void;
  onCancel: () => void;
  initialBuildId?: string;
}) {
  const [sourceType, setSourceType] = useState<EndpointType>('ehr');
  const [destType, setDestType] = useState<EndpointType>('build');

  // Source — EHR
  const [ehrId, setEhrId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState('');

  // Source — Build
  const [sourceBuildId, setSourceBuildId] = useState('');

  // Source — File
  const [fileSourceSystem, setFileSourceSystem] = useState('');

  // Destination — Build
  const [destBuildId, setDestBuildId] = useState(initialBuildId ?? '');

  // Meta
  const [titleSuffix, setTitleSuffix] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: ehrsData } = useQuery({ queryKey: ['ehrs'], queryFn: () => api.listEhrs() });
  const { data: buildsData } = useQuery({ queryKey: ['builds'], queryFn: () => api.listBuilds() });

  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];
  const builds: BuildRow[] = buildsData?.builds ?? [];
  const knownBuilds = builds.filter((b) => b.status === 'known');
  const selectedEhr = ehrs.find((e) => e.id === ehrId);
  const destBuild = builds.find((b) => b.id === destBuildId);
  const srcBuild = builds.find((b) => b.id === sourceBuildId);

  const autoBase =
    sourceType === 'ehr'   ? `${selectedEhr?.displayName ?? '…'} → ${destBuild?.displayName ?? (destType === 'file' ? 'CSV export' : '…')}` :
    sourceType === 'build' ? `${srcBuild?.displayName ?? '…'} → ${destBuild?.displayName ?? '…'}` :
    `${fileSourceSystem.trim() || 'Local file'} → ${destBuild?.displayName ?? (destType === 'file' ? 'CSV export' : '…')}`;

  const finalTitle = titleSuffix.trim() ? `${autoBase} — ${titleSuffix.trim()}` : autoBase;

  const sourceValid =
    sourceType === 'ehr'   ? !!ehrId && !!username && !!password :
    sourceType === 'build' ? !!sourceBuildId :
    true; // local-file source: files are uploaded on the session page, not here

  const destValid =
    destType === 'build' ? !!destBuildId :
    true;

  const canSubmit = sourceValid && destValid && !submitting;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      let sources: unknown[];
      let destinations: unknown[];

      if (sourceType === 'ehr') {
        sources = [{ kind: 'ehr', ehrId, ehrSlug: selectedEhr?.slug, loginUrl: loginUrl || selectedEhr?.loginUrl || undefined, username, password }];
      } else if (sourceType === 'build') {
        sources = [{ kind: 'cerbo', buildId: sourceBuildId }];
      } else {
        sources = [{ kind: 'csv', sourceSystem: fileSourceSystem.trim() || 'flat-file' }];
      }

      if (destType === 'build' && destBuildId) {
        destinations = [{ kind: 'cerbo', buildId: destBuildId }];
      } else {
        destinations = [];
      }

      const { session } = await api.createSession({
        buildId: destBuildId || undefined,
        title: finalTitle,
        sources,
        destinations,
      });
      onCreated(session.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast.error(`Failed to create session: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {/* Title preview + suffix */}
      <div className="mb-5 space-y-2">
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2.5"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-widest shrink-0" style={{ color: 'var(--text-dimmed)' }}>Title</span>
          <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{finalTitle}</span>
        </div>
        <Field label="Append note" hint="Optional — appended to the auto-generated title">
          <TextInput
            value={titleSuffix}
            onChange={setTitleSuffix}
            placeholder="e.g. initial migration, wave 2, test run…"
          />
        </Field>
      </div>
      <div className="border-t border-border mb-5" />

      {/* Two-column source → destination layout */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start">

        {/* ── Source ── */}
        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--accent-blue)' }}>
            From — Source
          </div>

          <TypeSelector value={sourceType} onChange={(t) => { setSourceType(t); if (t === 'build') setDestType('build'); }} options={SOURCE_TYPES} />

          {sourceType === 'ehr' && (
            <div className="space-y-2">
              <Field label="EHR">
                <Select value={ehrId} onChange={(v) => { setEhrId(v); setLoginUrl(''); }} required>
                  <option value="">Select EHR…</option>
                  {ehrs.map((e) => (
                    <option key={e.id} value={e.id}>{e.displayName}{e.recipePath ? ' ✓' : ''}</option>
                  ))}
                </Select>
              </Field>
              {selectedEhr && !selectedEhr.loginUrl && (
                <Field label="Login URL" hint="Required — no fixed URL on this EHR">
                  <TextInput value={loginUrl} onChange={setLoginUrl} placeholder="https://clinic.example.com/login" type="url" required mono />
                </Field>
              )}
              <Field label="Username">
                <TextInput value={username} onChange={setUsername} required />
              </Field>
              <Field label="Password">
                <TextInput value={password} onChange={setPassword} type="password" required />
              </Field>
            </div>
          )}

          {sourceType === 'build' && (
            <div className="space-y-2">
              <Field label="Source build">
                <Select value={sourceBuildId} onChange={setSourceBuildId} required>
                  <option value="">Select build…</option>
                  {knownBuilds.map((b) => (
                    <option key={b.id} value={b.id}>{b.displayName}</option>
                  ))}
                </Select>
              </Field>
              <WipBadge label="Build-to-build import creates a draft job." />
            </div>
          )}

          {sourceType === 'file' && (
            <Field
              label="Source system"
              hint="Where the data came from (e.g. kareo, tebra, optimantra-csv) — tags imported records for provenance + idempotency. Upload the file(s) on the session page after creating."
            >
              <TextInput value={fileSourceSystem} onChange={setFileSourceSystem} placeholder="flat-file" />
            </Field>
          )}
        </div>

        {/* ── Arrow ── */}
        <div className="flex flex-col items-center justify-start pt-10 gap-1 px-1">
          <div className="h-6 w-[1px]" style={{ background: 'var(--border)' }} />
          <span className="text-lg font-bold" style={{ color: 'var(--accent-blue)' }}>→</span>
          <div className="h-6 w-[1px]" style={{ background: 'var(--border)' }} />
        </div>

        {/* ── Destination ── */}
        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            To — Destination
          </div>

          <TypeSelector
            value={destType}
            onChange={setDestType}
            options={sourceType === 'build' ? DEST_TYPES.filter((t) => t.type !== 'file') : DEST_TYPES}
          />

          {destType === 'build' && (
            <div className="space-y-2">
              <Field label="Cerbo build">
                <Select value={destBuildId} onChange={setDestBuildId} required>
                  <option value="">Select build…</option>
                  {knownBuilds.map((b) => (
                    <option key={b.id} value={b.id}>{b.displayName}</option>
                  ))}
                </Select>
              </Field>
            </div>
          )}

          {destType === 'file' && (
            <WipBadge label="File export creates a draft job." />
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--accent-red)', background: 'rgba(238,93,80,0.08)', color: 'var(--accent-red)' }}>
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="text-sm px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
        >
          {submitting ? 'Creating…' : 'Create job'}
        </button>
      </div>
    </form>
  );
}
